import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { RotateCw, RotateCcw, ZoomIn, Check, X } from "lucide-react";

/**
 * Loads an image file into an HTMLImageElement, applying EXIF orientation
 * automatically via createImageBitmap when available (best-effort).
 */
async function fileToImage(file) {
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Produce a square 1:1 JPEG blob from source image + crop area + rotation.
 * Output is exactly 720x720 for the profile photo / carteirinha use case.
 */
async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0, outSize = 720) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext("2d");

  // Use an intermediate rotated canvas to bake in the rotation before cropping
  const rot = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rot));
  const cos = Math.abs(Math.cos(rot));
  const bbW = img.width * cos + img.height * sin;
  const bbH = img.width * sin + img.height * cos;
  const rotCanvas = document.createElement("canvas");
  rotCanvas.width = bbW;
  rotCanvas.height = bbH;
  const rctx = rotCanvas.getContext("2d");
  rctx.translate(bbW / 2, bbH / 2);
  rctx.rotate(rot);
  rctx.drawImage(img, -img.width / 2, -img.height / 2);

  ctx.drawImage(
    rotCanvas,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, outSize, outSize,
  );
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

export default function ImageCropper({ open, file, onCancel, onCropped, aspect = 1, title = "Ajustar foto" }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let objUrl;
    if (open && file) {
      setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setPixelCrop(null);
      fileToImage(file).then(({ url }) => { objUrl = url; setImageUrl(url); }).catch(() => {});
    }
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); if (!open) setImageUrl(null); };
  }, [open, file]);

  const onCropComplete = useCallback((_area, areaPixels) => { setPixelCrop(areaPixels); }, []);

  const confirm = async () => {
    if (!imageUrl || !pixelCrop) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageUrl, pixelCrop, rotation);
      onCropped?.(blob);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="image-cropper-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>
            Arraste para reposicionar, ajuste zoom e rotação. A foto final será quadrada.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full h-72 sm:h-80 bg-black rounded-2xl overflow-hidden">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          )}
        </div>

        <div className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-[hsl(var(--muted-foreground))] shrink-0" />
            <Slider
              value={[zoom]} min={1} max={4} step={0.05}
              onValueChange={([v]) => setZoom(v)}
              data-testid="cropper-zoom-slider"
              className="flex-1"
            />
          </div>
          <div className="flex items-center gap-3">
            <RotateCw className="h-4 w-4 text-[hsl(var(--muted-foreground))] shrink-0" />
            <Slider
              value={[rotation]} min={-180} max={180} step={1}
              onValueChange={([v]) => setRotation(v)}
              data-testid="cropper-rotation-slider"
              className="flex-1"
            />
            <div className="flex gap-1 shrink-0">
              <Button variant="outline" size="icon" className="rounded-full h-8 w-8"
                onClick={() => setRotation((r) => r - 90)}
                data-testid="cropper-rotate-left-btn"
                aria-label="Girar para esquerda">
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full h-8 w-8"
                onClick={() => setRotation((r) => r + 90)}
                data-testid="cropper-rotate-right-btn"
                aria-label="Girar para direita">
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onCancel?.()} className="rounded-full" data-testid="cropper-cancel-btn">
            <X className="h-4 w-4 mr-1.5" /> Cancelar
          </Button>
          <Button onClick={confirm} disabled={busy || !pixelCrop}
            className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
            data-testid="cropper-confirm-btn">
            <Check className="h-4 w-4 mr-1.5" /> {busy ? "Salvando…" : "Aplicar foto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
